import { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Plus, Minus, X, Info, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAppContext } from "@/lib/onboardingContext";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import {
  getCityStaffProfile,
  formatMonthlyRange,
  DEPARTMENT_ORDER,
  DEPARTMENT_LABELS,
  ESSENTIAL_ROLES,
  type CityRoleSalary,
  type StaffDepartment,
} from "@/lib/cityStaffData";
import HotelStaffingControl from "./HotelStaffingControl";

interface StaffRole {
  name: string;
  hourlyRange: [number, number];
  monthlyRange: [number, number];
  priority: "essential" | "recommended";
  description: string;
}

// Master role definitions (generic US baselines, used as fallback when no
// city-specific luxury data exists for the selected market).
const ROLES: Record<string, StaffRole> = {
  headChef:       { name: "Head Chef",           hourlyRange: [22, 35], monthlyRange: [3800, 6100], priority: "essential",    description: "Leads the kitchen, designs menu, manages food quality and costs." },
  lineCook:       { name: "Line Cook",           hourlyRange: [14, 20], monthlyRange: [2400, 3500], priority: "essential",    description: "Prepares dishes during service at assigned stations." },
  prepCook:       { name: "Prep Cook",           hourlyRange: [12, 16], monthlyRange: [2100, 2800], priority: "recommended",  description: "Handles ingredient prep, portioning, and mise en place before service." },
  server:         { name: "Server",              hourlyRange: [10, 15], monthlyRange: [1700, 2600], priority: "essential",    description: "Takes orders, serves guests, and manages front-of-house experience." },
  host:           { name: "Host/Hostess",        hourlyRange: [11, 15], monthlyRange: [1900, 2600], priority: "recommended",  description: "Greets guests, manages reservations, and controls seating flow." },
  busser:         { name: "Busser",              hourlyRange: [10, 14], monthlyRange: [1700, 2400], priority: "recommended",  description: "Clears and resets tables, supports servers during busy shifts." },
  dishwasher:     { name: "Dishwasher",          hourlyRange: [11, 14], monthlyRange: [1900, 2400], priority: "essential",    description: "Maintains clean dishes, utensils, and kitchen sanitation standards." },
  gm:             { name: "General Manager",     hourlyRange: [25, 40], monthlyRange: [4300, 6900], priority: "essential",    description: "Oversees daily operations, staffing, vendor relations, and finances." },
  sommelier:      { name: "Sommelier / Wine Dir.",hourlyRange: [20, 35], monthlyRange: [3500, 6100], priority: "essential",    description: "Curates the wine list, trains staff, and advises guests on pairings." },
  bartender:      { name: "Bartender",           hourlyRange: [12, 22], monthlyRange: [2100, 3800], priority: "essential",    description: "Mixes and serves drinks, manages the bar area." },
  barBack:        { name: "Bar Back",            hourlyRange: [10, 15], monthlyRange: [1700, 2600], priority: "recommended",  description: "Supports bartenders by restocking, cleaning, and prepping garnishes." },
  barista:        { name: "Barista",             hourlyRange: [12, 18], monthlyRange: [2100, 3100], priority: "essential",    description: "Prepares and serves coffee and espresso beverages." },
  baker:          { name: "Baker",               hourlyRange: [14, 22], monthlyRange: [2400, 3800], priority: "essential",    description: "Produces breads, pastries, and baked goods from scratch." },
  pastryChef:     { name: "Pastry Chef",         hourlyRange: [16, 26], monthlyRange: [2800, 4500], priority: "recommended",  description: "Specializes in desserts, pastries, and decorative baking." },
  cashier:        { name: "Cashier",             hourlyRange: [10, 14], monthlyRange: [1700, 2400], priority: "essential",    description: "Handles POS transactions, greets walk-in customers." },
  shiftLead:      { name: "Shift Lead",          hourlyRange: [14, 20], monthlyRange: [2400, 3500], priority: "recommended",  description: "Supervises staff during shifts, handles issues in manager's absence." },
  pizzaiolo:      { name: "Pizzaiolo",           hourlyRange: [16, 26], monthlyRange: [2800, 4500], priority: "essential",    description: "Specializes in dough preparation, oven management, and pizza making." },
  juiceBarista:   { name: "Juice / Smoothie Maker",hourlyRange: [11, 16], monthlyRange: [1900, 2800], priority: "essential",  description: "Prepares fresh juices, smoothies, and açaí bowls." },
  scooper:        { name: "Scooper / Counter Staff",hourlyRange: [10, 14], monthlyRange: [1700, 2400], priority: "essential", description: "Serves customers, portions ice cream, and maintains display cases." },
  truckDriver:    { name: "Truck Operator / Cook",hourlyRange: [14, 22], monthlyRange: [2400, 3800], priority: "essential",   description: "Drives the truck, cooks and serves food at locations." },
  stylist:        { name: "Stylist / Technician", hourlyRange: [14, 28], monthlyRange: [2400, 4800], priority: "essential",    description: "Provides beauty services to clients." },
  receptionist:   { name: "Receptionist",        hourlyRange: [11, 16], monthlyRange: [1900, 2800], priority: "recommended",  description: "Manages bookings, greets clients, handles check-in/out." },
  trainer:        { name: "Trainer / Instructor", hourlyRange: [18, 35], monthlyRange: [3100, 6100], priority: "essential",    description: "Leads classes, trains clients, designs fitness programs." },
  salesAssociate: { name: "Sales Associate",     hourlyRange: [11, 17], monthlyRange: [1900, 2900], priority: "essential",    description: "Assists customers, manages inventory, and drives sales." },
  storeManager:   { name: "Store Manager",       hourlyRange: [18, 30], monthlyRange: [3100, 5200], priority: "essential",    description: "Manages daily store operations, staff scheduling, and inventory." },
  stockClerk:     { name: "Stock Clerk",         hourlyRange: [11, 15], monthlyRange: [1900, 2600], priority: "recommended",  description: "Receives shipments, organizes stock, and maintains shelves." },
};

// Map business type IDs to their relevant roles
function getRolesForBusiness(businessType: string): StaffRole[] {
  switch (businessType) {
    case "wine-shop":
      return [ROLES.sommelier, ROLES.bartender, ROLES.barBack, ROLES.server, ROLES.host, ROLES.gm];

    case "bar":
      return [ROLES.bartender, ROLES.barBack, ROLES.server, ROLES.host, ROLES.busser, ROLES.gm];

    case "coffee-shop":
      return [ROLES.barista, ROLES.cashier, ROLES.shiftLead];

    case "bakery":
      return [ROLES.baker, ROLES.pastryChef, ROLES.cashier, ROLES.shiftLead];

    case "bakery-cafe":
      return [ROLES.baker, ROLES.barista, ROLES.cashier, ROLES.server, ROLES.shiftLead];

    case "pizzeria":
      return [ROLES.pizzaiolo, ROLES.lineCook, ROLES.prepCook, ROLES.server, ROLES.host, ROLES.dishwasher, ROLES.gm];

    case "juice-bar":
      return [ROLES.juiceBarista, ROLES.cashier, ROLES.shiftLead];

    case "ice-cream-shop":
      return [ROLES.scooper, ROLES.cashier, ROLES.shiftLead];

    case "food-truck":
      return [ROLES.truckDriver, ROLES.prepCook];

    case "fast-casual":
      return [ROLES.lineCook, ROLES.prepCook, ROLES.cashier, ROLES.shiftLead, ROLES.dishwasher];

    case "brunch-spot":
      return [ROLES.headChef, ROLES.lineCook, ROLES.prepCook, ROLES.server, ROLES.host, ROLES.busser, ROLES.bartender, ROLES.dishwasher, ROLES.gm];

    case "beauty-salon":
    case "nail-salon":
      return [ROLES.stylist, ROLES.receptionist, ROLES.storeManager];

    case "barber-shop":
      return [ROLES.stylist, ROLES.receptionist, ROLES.storeManager];

    case "fitness-studio":
      return [ROLES.trainer, ROLES.receptionist, ROLES.storeManager];

    case "retail-boutique":
    case "flower-shop":
    case "pet-store":
    case "bookstore":
    case "furniture-store":
    case "pop-up-shop":
      return [ROLES.salesAssociate, ROLES.cashier, ROLES.storeManager, ROLES.stockClerk];

    case "convenience-store":
    case "grocery-store":
    case "pharmacy":
      return [ROLES.cashier, ROLES.stockClerk, ROLES.storeManager, ROLES.shiftLead];

    case "restaurant":
    default:
      return [ROLES.headChef, ROLES.lineCook, ROLES.prepCook, ROLES.server, ROLES.host, ROLES.busser, ROLES.dishwasher, ROLES.gm];
  }
}

/** Roles that should always be considered essential in the city-data path. */
const CITY_ESSENTIAL_ROLES = ESSENTIAL_ROLES;

/**
 * Virtual Kitchen (ghost kitchen) role roster — no front-of-house. USD-anchored
 * monthly ranges; the UI converts to local currency at render time. Each role
 * carries a `department` so the collapsible grouped UI applies.
 */
interface GhostKitchenRole {
  name: string;
  monthlyRange: [number, number];
  hourlyRange: [number, number];
  priority: "essential" | "recommended";
  description: string;
  department: StaffDepartment;
}

const GHOST_KITCHEN_ROLES: GhostKitchenRole[] = [
  // KITCHEN
  { name: "Head Chef",                 department: "kitchen",    priority: "essential",   hourlyRange: [22, 35], monthlyRange: [3800, 6100], description: "Leads the kitchen, owns menu, manages food quality and costs." },
  { name: "Sous Chef",                 department: "kitchen",    priority: "essential",   hourlyRange: [18, 28], monthlyRange: [3100, 4800], description: "Second-in-command; runs service, training, and quality control." },
  { name: "Chef de Partie",            department: "kitchen",    priority: "recommended", hourlyRange: [15, 22], monthlyRange: [2600, 3800], description: "Owns a specific station (sauté, grill, fry, etc.)." },
  { name: "Line Cook",                 department: "kitchen",    priority: "essential",   hourlyRange: [14, 20], monthlyRange: [2400, 3500], description: "Prepares dishes during service at assigned stations." },
  { name: "Prep Cook",                 department: "kitchen",    priority: "recommended", hourlyRange: [12, 16], monthlyRange: [2100, 2800], description: "Handles ingredient prep, portioning, and mise en place." },
  { name: "Commis Chef",               department: "kitchen",    priority: "recommended", hourlyRange: [12, 16], monthlyRange: [2000, 2700], description: "Junior cook assisting senior chefs across stations." },
  { name: "Kitchen Porter / Dishwasher", department: "kitchen",  priority: "essential",   hourlyRange: [11, 14], monthlyRange: [1900, 2400], description: "Maintains clean dishes, equipment, and kitchen sanitation." },

  // OPERATIONS & LOGISTICS
  { name: "Kitchen Manager",           department: "operations", priority: "essential",   hourlyRange: [20, 32], monthlyRange: [3500, 5500], description: "Runs day-to-day kitchen operations, scheduling, and standards." },
  { name: "Operations Manager",        department: "operations", priority: "recommended", hourlyRange: [22, 35], monthlyRange: [3800, 6100], description: "Owns multi-brand workflows, throughput, and vendor coordination." },
  { name: "Packaging Coordinator",     department: "operations", priority: "recommended", hourlyRange: [13, 18], monthlyRange: [2200, 3100], description: "Manages packaging stock, assembly, and order accuracy." },
  { name: "Inventory Controller",      department: "operations", priority: "recommended", hourlyRange: [15, 22], monthlyRange: [2600, 3800], description: "Tracks stock levels, par sheets, waste, and reordering." },
  { name: "Delivery Coordinator",      department: "operations", priority: "recommended", hourlyRange: [14, 20], monthlyRange: [2400, 3500], description: "Coordinates dispatch with delivery platforms and couriers." },

  // ADMIN & TECH
  { name: "General Manager",                       department: "admin", priority: "recommended", hourlyRange: [25, 40], monthlyRange: [4300, 6900], description: "Oversees the business, P&L, vendors, and team." },
  { name: "Customer Service Agent",                department: "admin", priority: "recommended", hourlyRange: [13, 18], monthlyRange: [2200, 3100], description: "Handles guest issues across delivery platforms and DMs." },
  { name: "Social Media / Marketing Coordinator",  department: "admin", priority: "recommended", hourlyRange: [15, 24], monthlyRange: [2600, 4100], description: "Owns content, listings, promos, and platform optimisation." },
];

/**
 * Bed & Breakfast role roster — small-property hospitality with management,
 * front desk, housekeeping, breakfast service, and maintenance. USD-anchored
 * monthly ranges; the UI converts to local currency at render time.
 */
const BNB_ROLES: GhostKitchenRole[] = [
  // MANAGEMENT
  { name: "B&B Owner / Manager",   department: "management",       priority: "essential",   hourlyRange: [22, 38], monthlyRange: [3800, 6500], description: "Owns the property, sets direction, manages staff, vendors, and guest experience." },
  { name: "Assistant Manager",     department: "management",       priority: "recommended", hourlyRange: [16, 24], monthlyRange: [2800, 4100], description: "Supports the owner with daily operations, scheduling, and guest issues." },

  // FRONT DESK & GUEST SERVICES
  { name: "Front Desk Agent",          department: "frontDesk",    priority: "essential",   hourlyRange: [13, 18], monthlyRange: [2200, 3100], description: "Handles check-in/out, payments, and guest enquiries." },
  { name: "Guest Relations Officer",   department: "frontDesk",    priority: "recommended", hourlyRange: [14, 20], monthlyRange: [2400, 3500], description: "Personalises guest stays, handles VIPs and special requests." },
  { name: "Concierge / Reservations Agent", department: "frontDesk", priority: "recommended", hourlyRange: [14, 20], monthlyRange: [2400, 3500], description: "Manages bookings and helps guests with local recommendations." },

  // HOUSEKEEPING
  { name: "Head Housekeeper",      department: "housekeeping",     priority: "essential",   hourlyRange: [15, 22], monthlyRange: [2600, 3800], description: "Leads the housekeeping team, sets standards, audits room quality." },
  { name: "Room Attendant",        department: "housekeeping",     priority: "essential",   hourlyRange: [12, 16], monthlyRange: [2100, 2800], description: "Cleans and prepares guest rooms to standard." },
  { name: "Laundry Attendant",     department: "housekeeping",     priority: "recommended", hourlyRange: [11, 15], monthlyRange: [1900, 2600], description: "Handles linens, towels, and uniforms; runs laundry operations." },

  // KITCHEN & BREAKFAST
  { name: "Breakfast Cook",        department: "kitchenBreakfast", priority: "essential",   hourlyRange: [14, 20], monthlyRange: [2400, 3500], description: "Prepares breakfast service for guests daily." },
  { name: "Kitchen Assistant",     department: "kitchenBreakfast", priority: "recommended", hourlyRange: [12, 16], monthlyRange: [2100, 2800], description: "Supports the cook with prep, plating, and cleanup." },
  { name: "Waiter / Waitress",     department: "kitchenBreakfast", priority: "recommended", hourlyRange: [11, 15], monthlyRange: [1900, 2600], description: "Serves breakfast and assists guests in the dining area." },

  // MAINTENANCE
  { name: "Maintenance Technician", department: "maintenance",     priority: "essential",   hourlyRange: [16, 24], monthlyRange: [2800, 4100], description: "Handles HVAC, plumbing, electrical and equipment upkeep." },
  { name: "Handyman",               department: "maintenance",     priority: "recommended", hourlyRange: [13, 18], monthlyRange: [2200, 3100], description: "General repairs and small property fixes as needed." },
];

export default function StaffingControl() {
  const { onboarding } = useAppContext();
  const businessType = onboarding?.businessType || "restaurant";

  // Hotels use a fully different department-based planner.
  if (businessType === "hotel") {
    return <HotelStaffingControl />;
  }
  return <GenericStaffingControl />;
}

function GenericStaffingControl() {
  const { onboarding } = useAppContext();
  const city = onboarding?.city || "miami";
  const businessType = onboarding?.businessType || "restaurant";

  // Virtual Kitchens have no city-specific dataset and a unique role roster
  // (no front-of-house). They use the dedicated GHOST_KITCHEN_ROLES list with
  // department grouping, USD anchors converted to local currency at render.
  const isGhostKitchen = businessType === "ghost-kitchen";
  // Bed & Breakfasts use a dedicated small-property hospitality roster with
  // management / front desk / housekeeping / breakfast / maintenance.
  const isBnB = businessType === "bed-and-breakfast";

  // Prefer city-specific luxury data when available; ghost kitchens and B&Bs
  // skip this entirely. Other business types fall back to the generic roster.
  const cityProfile = useMemo(
    () => (isGhostKitchen || isBnB ? null : getCityStaffProfile(city)),
    [city, isGhostKitchen, isBnB],
  );
  const usingCityData = !!cityProfile;
  const useGroupedUI = usingCityData || isGhostKitchen || isBnB;


  const cs = cityProfile?.currencySymbol ?? getCurrencySymbol(city);

  // Build a unified list of "displayable roles" with monthly cost ranges in
  // the correct currency. For city data the values are already native; for
  // the fallback (and ghost kitchens) we convert USD values to local at render.
  type DisplayRole = {
    name: string;
    description: string;
    priority: "essential" | "recommended";
    monthlyMinDisplay: number;
    monthlyMaxDisplay: number;
    rangeLabel: string;
    department?: StaffDepartment;
  };

  const displayRoles: DisplayRole[] = useMemo(() => {
    if (cityProfile) {
      return cityProfile.roles.map((r: CityRoleSalary) => {
        const monthlyMin = Math.round(r.annualMin / 12);
        const monthlyMax = Math.round(r.annualMax / 12);
        return {
          name: r.role,
          description: `${r.role} — luxury / 5-star tier compensation in ${cityProfile.label}.`,
          priority: CITY_ESSENTIAL_ROLES.has(r.role) ? "essential" as const : "recommended" as const,
          monthlyMinDisplay: monthlyMin,
          monthlyMaxDisplay: monthlyMax,
          rangeLabel: formatMonthlyRange(r, cityProfile.currencySymbol),
          department: r.department,
        };
      });
    }
    if (isGhostKitchen || isBnB) {
      const roster = isBnB ? BNB_ROLES : GHOST_KITCHEN_ROLES;
      return roster.map(r => {
        const monthlyMin = convertFromUSD(r.monthlyRange[0], city);
        const monthlyMax = convertFromUSD(r.monthlyRange[1], city);
        const hourlyMin = convertFromUSD(r.hourlyRange[0], city);
        const hourlyMax = convertFromUSD(r.hourlyRange[1], city);
        return {
          name: r.name,
          description: r.description,
          priority: r.priority,
          monthlyMinDisplay: monthlyMin,
          monthlyMaxDisplay: monthlyMax,
          rangeLabel: `${cs}${hourlyMin}–${cs}${hourlyMax}/hr · ${cs}${monthlyMin.toLocaleString()}–${cs}${monthlyMax.toLocaleString()}/mo`,
          department: r.department,
        };
      });
    }
    // Fallback: generic role definitions, converted from USD.
    return getRolesForBusiness(businessType).map(r => {
      const monthlyMin = convertFromUSD(r.monthlyRange[0], city);
      const monthlyMax = convertFromUSD(r.monthlyRange[1], city);
      const hourlyMin = convertFromUSD(r.hourlyRange[0], city);
      const hourlyMax = convertFromUSD(r.hourlyRange[1], city);
      return {
        name: r.name,
        description: r.description,
        priority: r.priority,
        monthlyMinDisplay: monthlyMin,
        monthlyMaxDisplay: monthlyMax,
        rangeLabel: `${cs}${hourlyMin}–${cs}${hourlyMax}/hr · ${cs}${monthlyMin.toLocaleString()}–${cs}${monthlyMax.toLocaleString()}/mo`,
      };
    });
  }, [cityProfile, isGhostKitchen, isBnB, businessType, city, cs]);

  // Group roles by department for collapsible rendering when departments are
  // present (city-data restaurants and virtual kitchens).
  const groupedRoles = useMemo(() => {
    if (!useGroupedUI) return null;
    const groups = new Map<StaffDepartment, DisplayRole[]>();
    for (const r of displayRoles) {
      if (!r.department) continue;
      const arr = groups.get(r.department) ?? [];
      arr.push(r);
      groups.set(r.department, arr);
    }
    return DEPARTMENT_ORDER
      .filter(d => groups.has(d))
      .map(d => ({ department: d, roles: groups.get(d)! }));
  }, [displayRoles, useGroupedUI]);

  // Track which department sections are open (default: all open).
  const [openDepts, setOpenDepts] = useState<Record<StaffDepartment, boolean>>(() => ({
    kitchen: true, floor: true, bar: true, sommelier: true, operations: true, admin: true,
    management: true, frontDesk: true, housekeeping: true, kitchenBreakfast: true, maintenance: true,
  }));
  const toggleDept = (d: StaffDepartment) =>
    setOpenDepts(prev => ({ ...prev, [d]: !prev[d] }));


  const [counts, setCounts] = useState<Record<string, number>>({});

  interface CustomRole { id: string; name: string; description: string; monthlyCost: number; count: number; }
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [draft, setDraft] = useState({ name: "", description: "", monthlyCost: "" });

  // Reset counts when the displayed role list changes (city or business type).
  useMemo(() => {
    setCounts(Object.fromEntries(displayRoles.map(r => [r.name, r.priority === "essential" ? 1 : 0])));
  }, [displayRoles]);

  const update = useCallback((name: string, delta: number) => {
    setCounts(prev => ({ ...prev, [name]: Math.max(0, (prev[name] || 0) + delta) }));
  }, []);

  const updateCustomCount = (id: string, delta: number) => {
    setCustomRoles(prev => prev.map(r => r.id === id ? { ...r, count: Math.max(0, r.count + delta) } : r));
  };

  const removeCustomRole = (id: string) => {
    setCustomRoles(prev => prev.filter(r => r.id !== id));
  };

  const addCustomRole = () => {
    if (!draft.name.trim()) return;
    setCustomRoles(prev => [...prev, {
      id: crypto.randomUUID(),
      name: draft.name.trim(),
      description: draft.description.trim(),
      monthlyCost: Number(draft.monthlyCost) || 0,
      count: 1,
    }]);
    setDraft({ name: "", description: "", monthlyCost: "" });
  };

  // Total monthly is computed in **display currency** so we can render it
  // directly without re-converting.
  const totalMonthly = useMemo(() => {
    const base = displayRoles.reduce((sum, role) => {
      const count = counts[role.name] || 0;
      const avg = (role.monthlyMinDisplay + role.monthlyMaxDisplay) / 2;
      return sum + count * avg;
    }, 0);
    const custom = customRoles.reduce((sum, r) => sum + r.count * r.monthlyCost, 0);
    return base + custom;
  }, [counts, displayRoles, customRoles]);

  const totalStaff = useMemo(() => {
    const base = Object.values(counts).reduce((a, b) => a + b, 0);
    const custom = customRoles.reduce((a, r) => a + r.count, 0);
    return base + custom;
  }, [counts, customRoles]);

  return (
    <TooltipProvider delayDuration={200}>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}
      className="bg-card rounded-xl p-6 border border-border shadow-card mb-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-medium text-foreground">
            Staffing Control
            {usingCityData && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">· {cityProfile.label} rates</span>
            )}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">{totalStaff} staff</div>
          <div className="text-sm font-medium text-primary">{cs}{Math.round(totalMonthly).toLocaleString()}/mo</div>
        </div>
      </div>

      {/* Dubai (or any "package market") tooltip banner */}
      {usingCityData && cityProfile.packageMarket && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-foreground">
            <span className="font-medium">Package roles:</span>{" "}
            <span className="text-muted-foreground">
              Packages typically include housing, flights &amp; health insurance on top of the listed salary.
            </span>
          </p>
        </div>
      )}

      <div className="space-y-3">
        {(() => {
          const renderRoleRow = (role: DisplayRole) => {
            const count = counts[role.name] || 0;
            return (
              <div key={role.name} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{role.name}</span>
                    <Badge variant={role.priority === "essential" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
                      {role.priority}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{role.description}</p>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {role.rangeLabel}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => update(role.name, -1)} disabled={count === 0}>
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-6 text-center text-sm font-medium text-foreground">{count}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => update(role.name, 1)}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          };

          // Grouped + collapsible rendering when city data provides departments.
          if (groupedRoles && groupedRoles.length > 0) {
            return groupedRoles.map(({ department, roles }) => {
              const open = openDepts[department];
              const sectionStaff = roles.reduce((s, r) => s + (counts[r.name] || 0), 0);
              return (
                <Collapsible key={department} open={open} onOpenChange={() => toggleDept(department)}>
                  <CollapsibleTrigger className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted border border-border/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
                      <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
                        {DEPARTMENT_LABELS[department]}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {roles.length} role{roles.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    {sectionStaff > 0 && (
                      <span className="text-[11px] font-medium text-primary">{sectionStaff} staff</span>
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-2">
                    {roles.map(renderRoleRow)}
                  </CollapsibleContent>
                </Collapsible>
              );
            });
          }

          // Flat rendering for fallback (non-restaurant business types).
          return displayRoles.map(renderRoleRow);
        })()}


        {/* Custom roles */}
        {customRoles.map(role => (
          <div key={role.id} className="flex items-center gap-3 p-3 rounded-lg bg-accent/20 border border-accent/40">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground">{role.name}</span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">custom</Badge>
              </div>
              {role.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{role.description}</p>}
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {cs}{role.monthlyCost.toLocaleString()}/mo per person
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeCustomRole(role.id)}>
                <X className="w-3 h-3" />
              </Button>
              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateCustomCount(role.id, -1)} disabled={role.count === 0}>
                <Minus className="w-3 h-3" />
              </Button>
              <span className="w-6 text-center text-sm font-medium text-foreground">{role.count}</span>
              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateCustomCount(role.id, 1)}>
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}

        {/* Add custom position form */}
        <div className="p-3 rounded-lg border border-dashed border-border bg-muted/10 space-y-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Add custom position</span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input
              placeholder="Role name"
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              className="h-8 text-sm"
            />
            <Input
              placeholder="Description"
              value={draft.description}
              onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
              className="h-8 text-sm"
            />
            <div className="flex gap-2">
              <Input
                placeholder={`${cs} monthly cost`}
                type="number"
                value={draft.monthlyCost}
                onChange={e => setDraft(d => ({ ...d, monthlyCost: e.target.value }))}
                className="h-8 text-sm flex-1"
              />
              <Button size="sm" className="h-8 px-3" onClick={addCustomRole} disabled={!draft.name.trim()}>
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border flex justify-between items-center">
        <span className="text-sm font-medium text-foreground">Est. Total Monthly Labor</span>
        <span className="text-lg font-medium text-primary">{cs}{Math.round(totalMonthly).toLocaleString()}/mo</span>
      </div>

      {/* Global footnote — applies to every city using the luxury dataset. */}
      {usingCityData && (
        <div className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3 h-3 mt-0.5 shrink-0 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              Salaries reflect typical 2025/2026 luxury hospitality benchmarks. Junior tiers, mid-market venues, and casual concepts will pay less.
            </TooltipContent>
          </Tooltip>
          <span>
            Ranges reflect luxury / 5-star establishments. Actual compensation may vary based on experience and property tier.
          </span>
        </div>
      )}
    </motion.div>
    </TooltipProvider>
  );
}
